// @ts-nocheck
import { useCallback, useEffect, useRef } from "react";

/**
 * Returns a function that schedules a callback to run after the browser
 * completes a full paint cycle (double-requestAnimationFrame pattern).
 *
 * Similar in spirit to `setTimeout(fn, 0)`, but more reliable for
 * layout-dependent work because it ties execution to the browser's
 * rendering pipeline rather than the generic task queue. `setTimeout`
 * has no guaranteed relationship to when the browser paints — it may
 * fire before or after a render frame unpredictably. Double-rAF
 * guarantees that at least one full render cycle (style recalc → layout
 * → paint) has completed before the callback executes, so DOM
 * measurements (e.g. `getBoundingClientRect`) reflect the final state.
 *
 * Useful for triggering layout-dependent recalculations after DOM changes
 * that need one full paint to stabilize (e.g. elements transitioning from
 * `display:none` to visible, or container size changes from flex reflow).
 *
 * Pending frames are automatically cancelled on unmount, and rapid
 * successive calls are deduplicated (only the latest wins).
 */
const useAfterPaint = () => {
    const frameRef = useRef<number>(0);

    useEffect(() => {
        return () => {
            cancelAnimationFrame(frameRef.current);
        };
    }, []);

    return useCallback((callback: () => void) => {
        cancelAnimationFrame(frameRef.current);
        frameRef.current = requestAnimationFrame(() => {
            frameRef.current = requestAnimationFrame(callback);
        });
    }, []);
};

export default useAfterPaint;
